import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  Customer,
  CustomerComplaint,
  CustomerListParams,
  CustomerRefund,
} from '../types/customer.types';

/** Customer reads — the Admin API limits them to the staff member's countries. */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export async function getCustomers(
  params: CustomerListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Customer>> {
  const page = await http.get<AdminPage<Customer>>('/admin/customers', { params, signal });
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

export async function getCustomer(customerId: number, signal?: AbortSignal): Promise<Customer> {
  return http.get<Customer>(`/admin/customers/${customerId}`, { signal });
}

/** The same search as Excel (reports.export). The backend records the export itself. */
export async function exportCustomers(params: Omit<CustomerListParams, 'page' | 'pageSize'>) {
  const { blob, fileName } = await http.download('/admin/customers/export', {
    params: { ...params, format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? 'customers.xlsx' };
}

/** Deactivate (their session can no longer be renewed) or reactivate. customers.manage; audited. */
export async function setCustomerStatus(customerId: number, isActive: boolean, reason: string) {
  return http.post<{ changed: number }>(`/admin/customers/${customerId}/status`, {
    isActive,
    reason,
  });
}

/**
 * Sends the storefront's own password-reset code through the country's channel
 * (SMS or email). Staff never see or set a password. Returns the masked destination.
 */
export async function sendCustomerPasswordReset(customerId: number, reason: string) {
  return http.post<string>(`/admin/customers/${customerId}/password-reset`, { reason });
}

export async function getCustomerComplaints(
  customerId: number,
  signal?: AbortSignal,
): Promise<CustomerComplaint[]> {
  const page = await http.get<AdminPage<CustomerComplaint>>('/admin/complaints', {
    params: { customerId, page: 1, pageSize: 20 },
    signal,
  });
  return page.items;
}

export async function getCustomerRefunds(
  customerId: number,
  signal?: AbortSignal,
): Promise<CustomerRefund[]> {
  const page = await http.get<AdminPage<CustomerRefund>>('/admin/refunds', {
    params: { customerId, page: 1, pageSize: 20 },
    signal,
  });
  return page.items;
}
